import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle, ShoppingBag, RefreshCw } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMerchCart } from '../context/MerchCartContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MerchOrderStatusPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('loading');
  const [checking, setChecking] = useState(false);
  const { clearCart } = useMerchCart();

  const verify = async () => {
    setChecking(true);
    try {
      const v = await axios.post(`${API}/api/merch/orders/${orderId}/verify-payment`);
      const o = await axios.get(`${API}/api/merch/orders/${orderId}`);
      setOrder(o.data);
      setStatus(v.data.status || o.data.status);
      if ((v.data.status || o.data.status) === 'approved') clearCart();
    } catch {
      setStatus('error');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { if (orderId) verify(); else setStatus('error'); }, [orderId]); // eslint-disable-line

  const approved = status === 'approved';
  const notFound = status === 'error';
  const failed = ['rejected', 'cancelled', 'refunded'].includes(status);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pt-28 md:pt-36 pb-20 px-6 max-w-xl mx-auto" data-testid="merch-order-status-page">
        {status === 'loading' ? (
          <p className="text-white/50 text-center">Verificando tu pago…</p>
        ) : (
          <div className="bg-[#0B0B0B] border border-[#27272A] rounded-2xl overflow-hidden">
            <div className={`h-1.5 ${approved ? 'bg-emerald-500' : (failed || notFound) ? 'bg-red-500' : 'bg-[#D4AF37]'}`} />
            <div className="p-8 text-center">
              {approved ? <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-4" /> : (failed || notFound) ? <XCircle size={48} className="mx-auto text-red-400 mb-4" /> : <Clock size={48} className="mx-auto text-[#D4AF37] mb-4" />}
              <h1 className="text-white text-2xl font-black uppercase mb-2">
                {approved ? '¡Compra confirmada!' : notFound ? 'Pedido no encontrado' : failed ? 'Pago no completado' : 'Pago pendiente'}
              </h1>
              <p className="text-white/50 text-sm mb-6">
                {approved ? 'Te enviamos el detalle y las instrucciones por email.' : notFound ? 'No pudimos encontrar este pedido. Verificá el link o escribinos a central@bravoproductora.com.' : failed ? 'El pago fue rechazado o cancelado. Podés volver a intentarlo.' : 'Tu pago aún está siendo procesado por Mercado Pago.'}
              </p>

              {approved && order?.pickup_code && (
                <div className="border-2 border-dashed border-[#D4AF37]/50 bg-[#D4AF37]/5 rounded-2xl p-6 mb-6" data-testid="pickup-code-box">
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] mb-2">Tu código de retiro</p>
                  <p className="text-[#D4AF37] font-mono text-3xl font-black tracking-[0.3em]" data-testid="pickup-code">{order.pickup_code}</p>
                  <p className="text-white/40 text-xs mt-2">Mostralo en el stand de merch para retirar tu compra</p>
                  <p className="text-white/60 text-xs mt-3 border-t border-[#D4AF37]/20 pt-3" data-testid="pickup-rep-note">
                    Un representante de Bravo se va a comunicar con vos <span className="text-[#D4AF37] font-bold">vía Mail o WhatsApp</span> para coordinar el retiro.
                  </p>
                </div>
              )}
              {approved && order?.delivery_method === 'shipping' && (
                <div className="border border-[#27272A] bg-[#121212] rounded-2xl p-5 mb-6 text-left">
                  <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] mb-1 font-bold">Envío a domicilio</p>
                  <p className="text-white/70 text-sm">{order.shipping_address?.street}, {order.shipping_address?.city}</p>
                  <p className="text-white/40 text-xs mt-1">Un representante de Bravo se va a comunicar con vos <span className="text-[#D4AF37] font-bold">vía Mail o WhatsApp</span> para coordinar la entrega.</p>
                </div>
              )}

              {order && (
                <div className="text-left border-t border-[#27272A] pt-4 mb-6">
                  {(order.items || []).map((it, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm py-1.5">
                      {it.image ? (
                        <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover border border-white/10 flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#151515] border border-white/5 flex-shrink-0" />
                      )}
                      <span className="text-white/70 flex-1 min-w-0 truncate">{it.name}{it.variant ? ` (${it.variant})` : ''} <span className="text-white/30">x{it.quantity}</span></span>
                      <span className="text-white/70 flex-shrink-0">${Number(it.subtotal).toLocaleString('es-AR')}</span>
                    </div>
                  ))}
                  {order.shipping_cost > 0 && <div className="flex justify-between text-sm py-1.5"><span className="text-white/50">Envío</span><span className="text-white/50">${Number(order.shipping_cost).toLocaleString('es-AR')}</span></div>}
                  <div className="flex justify-between font-black pt-2 border-t border-[#27272A] mt-2"><span className="text-white uppercase">Total</span><span className="text-[#D4AF37]" data-testid="order-total">${Number(order.total_amount).toLocaleString('es-AR')}</span></div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {!approved && !failed && !notFound && (
                  <button onClick={verify} disabled={checking} className="px-6 py-3 bg-[#D4AF37] text-black rounded-full font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="recheck-payment-btn">
                    <RefreshCw size={14} className={checking ? 'animate-spin' : ''} /> Ya pagué — verificar de nuevo
                  </button>
                )}
                <Link to="/tienda" className="px-6 py-3 border border-white/15 text-white rounded-full font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:border-[#D4AF37]" data-testid="back-to-store-link">
                  <ShoppingBag size={14} /> {(failed || notFound) ? 'Volver a la tienda' : 'Seguir comprando'}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
